import { Suspense, lazy, useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ErrorPage from "./pages/Error/ErrorPage";
import Loader from "./common/Loader";
import routes from "./routes";
import ErrorBoundary from "./components/ErrorBoundary";
import ConditionalHome from "./components/ConditionalHome";
import { setLanguage } from "./redux/languageSlice";
import i18n from "./i18n";

import SignIn from "./pages/Authentication/SignIn";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { userAuthAPI } from "./api/userAuthAPI";
import { getCookieValue } from "./js/utils/cookies";
import useUserStore from "./zustand/UseUserStore";
import { SESSION_ID } from "./api/apiConfig";
import { ConfettiProvider } from "./context/ConfettiContext";
import { CookieConsentProvider } from "./context/CookieConsentContext";
import ConfettiRain from "./components/ConfettiRain";
import { EventTrackingInitializer } from "./components/EventTrackingInitializer";
// import Backpack from "./components/Backpack";

const Dashboardlayout = lazy(() => import("./layout/DashboardLayout"));

function App() {
	const [loading, setLoading] = useState(true);
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const location = useLocation();
	const fingerprintRef = useRef(null);
	const fingerprintInitializedRef = useRef(false);
	const state = useUserStore();
	const navigate = useNavigate();

	// Function to set a cookie - mobile-friendly version
	const setCookieFunction = (name, value, days) => {
		let expires = "";
		if (days) {
			const date = new Date();
			date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
			expires = "; expires=" + date.toUTCString();
		}

		// Mobile-friendly cookie settings
		const cookieString = `${name}=${value || ""}${expires}; path=/; SameSite=Lax`;
		document.cookie = cookieString;

		// Also store in localStorage as backup for mobile devices
		try {
			localStorage.setItem(name, value || "");
		} catch (error) {
			console.warn("Failed to set localStorage:", error);
		}
	};

	const handleGetAccount = async (userID) => {
		const response = await userAuthAPI.getaccount(userID);
		state.setUserWithRoles(response.result.member, response.result.user_roles);
		if (response.result.avatar) {
			state.buildCharacter(response.result.avatar);
		}

		// Use the reusable method from the store
		state.setupUserProgressFromAccount(response);
		// if (response.result.member.Zipcode) {
		// 	state.setAccessLevel(2);
		// } else {
		// 	state.setAccessLevel(1);
		// }
	};

	// Move initFingerprint outside useEffect so it can be called elsewhere
	const initFingerprint = async () => {
		if (fingerprintRef.current === null && !fingerprintInitializedRef.current) {
			fingerprintInitializedRef.current = true;

			let fingerprint = null;
			try {
				const fp = await FingerprintJS.load();
				const result = await fp.get();
				fingerprint = result.visitorId;
				fingerprintRef.current = fingerprint;
			} catch (error) {
				console.error("Fingerprint generation failed:", error);
				// Fallback: generate a simple fingerprint for mobile compatibility
				fingerprint = `mobile_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
				fingerprintRef.current = fingerprint;
			}

			// Make session validation request once fingerprint is available
			const localsessionid = SESSION_ID();
			console.log("localsessionid", localsessionid);
			console.log("Generated fingerprint:", fingerprint);

			try {
				const response = await userAuthAPI.sessionvalidation(
					fingerprint,
					localsessionid
				);
				const newSessionid = response.result.sessionid;
				const memberid = response.result.memberID;
				const lastVisitedPage = response.result.lastVisitedPage;

				// Set the session ID cookie and localStorage (mobile-friendly approach)
				setCookieFunction("goodglows_sessionid", newSessionid, 3);
				localStorage.setItem("goodglows_sessionid", newSessionid);
				console.log("newSessionid", newSessionid);
				console.log("Member ID:", memberid);
				console.log("Last Visited Page:", lastVisitedPage);

				if (memberid && memberid !== 0) {
					// Existing user - get their account
					await handleGetAccount(memberid);

					// Navigate to last visited page if available
					// Only navigate if we're on entry pages (root, sign-in, story-landing)
					// This allows the app to restore the user's previous location
					const currentPath = location.pathname;
					const isEntryPage =
						currentPath === "/" ||
						currentPath === "/signin" ||
						currentPath === "/story-landing";

					if (
						isEntryPage &&
						lastVisitedPage &&
						lastVisitedPage !== "/" &&
						lastVisitedPage !== currentPath
					) {
						console.log("📍 Navigating to last visited page:", lastVisitedPage);
						navigate(lastVisitedPage);
					}
				} else {
					// For new users (memberID === 0), store the last visited page for after sign-in
					if (lastVisitedPage && lastVisitedPage !== "/") {
						localStorage.setItem("returnAfterSignIn", lastVisitedPage);
						console.log(
							"📍 Stored last visited page for new user:",
							lastVisitedPage
						);
					}
				}
			} catch (error) {
				console.error("Session validation error:", error);
				// For mobile, if session validation fails but we have an existing session,
				// try to auto-sign in with the existing session
				if (localsessionid) {
					console.log("Attempting auto-signin with existing session...");
					try {
						const autoSigninResponse =
							await userAuthAPI.autosignin(localsessionid);
						if (
							autoSigninResponse.success &&
							autoSigninResponse.result?.memberID
						) {
							console.log(
								"Auto-signin successful:",
								autoSigninResponse.result.memberID
							);
							handleGetAccount(autoSigninResponse.result.memberID);
						}
					} catch (autoSigninError) {
						console.error("Auto-signin also failed:", autoSigninError);
					}
				}
			}
			console.log("Fingerprint:", fingerprintRef.current);
		}
	};

	useEffect(() => {
		setTimeout(() => setLoading(false), 500); // TODO: remove this when we have a real loading state later

		const savedLanguage = localStorage.getItem("language");
		if (savedLanguage) {
			dispatch(setLanguage(savedLanguage));
			i18n.changeLanguage(savedLanguage);
		} else {
			dispatch(setLanguage(navigator.language.split("-")[0]));
			i18n.changeLanguage(navigator.language.split("-")[0]);
		}

		// Check if there's already a valid session ID
		const existingSessionId =
			getCookieValue("goodglows_sessionid") ||
			localStorage.getItem("goodglows_sessionid");
		const accepted = localStorage.getItem("cookiesaccepted");

		// Check if user is on an invite URL
		const isInviteURL = location.pathname.startsWith("/accept-invite");

		// Initialize fingerprint - cookie consent is now handled in individual pages (story-landing, etc.)
		// Only auto-accept cookies for invite URLs to ensure they work smoothly
		if (isInviteURL && accepted !== "true") {
			localStorage.setItem("cookiesaccepted", "true");
		}

		// Initialize fingerprint regardless of cookie acceptance for session tracking
		initFingerprint();

		// // Check if user is already logged in
		// const sessionid = getCookieValue("sessionID");
		// if (sessionid) {
		// 	// Try to auto-login with the existing session
		// 	const attemptAutoLogin = async () => {
		// 		try {
		// 			const response = await autoLogin(sessionid);
		// 			console.log("Auto login response:", response);
		// 			setLoggedIn(true);
		// 		} catch (error) {
		// 			setLoggedIn(false);
		// 		}
		// 	};
		// 	attemptAutoLogin();
		// }
	}, [dispatch, location.pathname]);

	return loading ? (
		<Loader />
	) : (
		<ConfettiProvider>
			<CookieConsentProvider>
				<EventTrackingInitializer />
				<ToastContainer
					position="bottom-right"
					autoClose={3000}
					hideProgressBar={true}
					newestOnTop={false}
					closeOnClick
					rtl={false}
					pauseOnFocusLoss
					draggable
					pauseOnHover
					theme="light"
				/>
				{/* <Backpack /> */}
				<ConfettiRain />
				<ErrorBoundary>
					<Routes>
						<Route element={<Dashboardlayout />}>
							<Route path="/" element={<ConditionalHome />} />

							<Route path="/signin" element={<SignIn />} />
							{routes.map((routes, index) => {
								const { path, component: Component, props = {} } = routes;

								// You can add dynamic props here based on state or context
								const dynamicProps = {
									...props,
									// Add dynamic props from your app state
									user: state.user,
									isAuthenticated: state.userProgress.isAuthenticated,
									// Add any other dynamic props you need
								};

								return (
									<Route
										key={index}
										path={path}
										element={
											<Suspense fallback={<Loader />}>
												<Component {...dynamicProps} />
											</Suspense>
										}
									/>
								);
							})}
						</Route>
						<Route path="*" element={<ErrorPage />} />
					</Routes>
				</ErrorBoundary>
			</CookieConsentProvider>
		</ConfettiProvider>
	);
}

export default App;
